/**
 * PDF Download Utilities
 * 
 * Utilities for downloading PDF documents generated with @react-pdf/renderer.
 * Provides a clean interface to replace the old window.print() approach.
 */

import { pdf } from '@react-pdf/renderer';
import { ReactElement } from 'react';

/**
 * Download a PDF document
 * @param pdfDocument - React element created with @react-pdf/renderer components
 * @param filename - Name of the file to download (without .pdf extension)
 * @returns Promise that resolves when download starts
 */
export const downloadPDF = async (pdfDocument: ReactElement, filename: string): Promise<void> => {
  try {
    // Generate PDF blob
    const blob = await pdf(pdfDocument).toBlob();
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    
    // Trigger download
    window.document.body.appendChild(link);
    link.click();
    
    // Cleanup
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw new Error('Failed to download PDF');
  }
};

/**
 * Generate PDF blob for custom handling
 * @param pdfDocument - React element created with @react-pdf/renderer components
 * @returns Promise<Blob> - PDF blob
 */
export const generatePDFBlob = async (pdfDocument: ReactElement): Promise<Blob> => {
  try {
    return await pdf(pdfDocument).toBlob();
  } catch (error) {
    console.error('Error generating PDF blob:', error);
    throw new Error('Failed to generate PDF');
  }
};

/**
 * Open PDF in new tab/window
 * @param pdfDocument - React element created with @react-pdf/renderer components
 * @param filename - Name for the document (optional)
 * @returns Promise that resolves when PDF opens
 */
export const openPDFInNewTab = async (pdfDocument: ReactElement, filename?: string): Promise<void> => {
  try {
    const blob = await pdf(pdfDocument).toBlob();
    const url = URL.createObjectURL(blob);
    
    const newWindow = window.open(url, '_blank');
    if (newWindow && filename) {
      newWindow.document.title = filename;
    }
    
    // Note: URL cleanup will happen when the window is closed
    // We can't immediately revoke it as the new tab needs it
  } catch (error) {
    console.error('Error opening PDF in new tab:', error);
    throw new Error('Failed to open PDF');
  }
};

/**
 * PDF generation status enum
 */
export enum PDFGenerationStatus {
  IDLE = 'idle',
  GENERATING = 'generating',
  SUCCESS = 'success',
  ERROR = 'error'
}

/**
 * Hook-like interface for PDF generation state management
 * (This is a simple state object, not a React hook)
 */
export interface PDFGenerationState {
  status: PDFGenerationStatus;
  error?: string;
}

/**
 * Create initial PDF generation state
 */
export const createPDFGenerationState = (): PDFGenerationState => ({
  status: PDFGenerationStatus.IDLE
});

/**
 * Utility to get user-friendly status messages
 */
export const getPDFStatusMessage = (status: PDFGenerationStatus, locale: string = 'fr'): string => {
  const messages = {
    fr: {
      [PDFGenerationStatus.IDLE]: 'Prêt',
      [PDFGenerationStatus.GENERATING]: 'Génération du PDF...',
      [PDFGenerationStatus.SUCCESS]: 'PDF généré avec succès',
      [PDFGenerationStatus.ERROR]: 'Erreur lors de la génération du PDF'
    },
    en: {
      [PDFGenerationStatus.IDLE]: 'Ready',
      [PDFGenerationStatus.GENERATING]: 'Generating PDF...',
      [PDFGenerationStatus.SUCCESS]: 'PDF generated successfully',
      [PDFGenerationStatus.ERROR]: 'Error generating PDF'
    }
  };

  return messages[locale as keyof typeof messages]?.[status] || messages.en[status];
};
